"use client";

import { hasActiveConfiguredFilterValues } from "@/components/filters/filter-values";
import { HorizontalOverflowLane } from "@/components/list/HorizontalOverflowLane";
import { PageRow } from "@/components/shell";
import type { ListDisplayController } from "@/hooks/useListController";
import type { AnyLabeledFilterConfig } from "@/types/list";
import cn from "twsx";
import { ListDisplayControls } from "./ListDisplayControls";
import type { ExportMutation } from "./ListDisplayControls";
import { ListQueryControls } from "./ListQueryControls";

type ListHeaderControlsProps<$Filters> = {
  filterConfigs: Record<string, AnyLabeledFilterConfig>;
  filterComponents: Record<string, React.ComponentType<any>>;
  filterInput?: Record<string, unknown>;
  currentFilters?: $Filters;
  currentSearch?: string;
  onFilterChange?: (values: $Filters) => void;
  className?: string;
  display?: ListDisplayController;
  exportMutation?: ExportMutation;
  hiddenFilterKeys?: string[];
  standaloneFilterKeys?: string[];
  showActiveClauses?: boolean;
  showStandaloneFilters?: boolean;
  showTrigger?: boolean;
  wrapWithParent?: boolean;
};

type ListHeaderActiveFiltersRowProps<$Filters> = ListHeaderControlsProps<$Filters> & {
  hiddenFilterKeys?: string[];
};

export function ListHeaderControls<$Filters>({
  filterConfigs,
  filterComponents,
  filterInput,
  currentFilters,
  currentSearch,
  onFilterChange,
  className,
  display,
  exportMutation,
  hiddenFilterKeys = [],
  standaloneFilterKeys = [],
  showActiveClauses = true,
  showStandaloneFilters = true,
  showTrigger = true,
  wrapWithParent = true,
}: ListHeaderControlsProps<$Filters>) {
  const hasFilters = Object.keys(filterConfigs).length > 0;

  if (!hasFilters && !display && !exportMutation) {
    return null;
  }

  const controls = (
    <>
      {hasFilters && (
        <ListQueryControls
          filterConfigs={filterConfigs}
          filterComponents={filterComponents}
          filterInput={filterInput}
          currentFilters={currentFilters}
          onFilterChange={onFilterChange}
          showTrigger={showTrigger}
          showActiveClauses={showActiveClauses}
          showStandaloneFilters={showStandaloneFilters}
          hiddenFilterKeys={hiddenFilterKeys}
          standaloneFilterKeys={standaloneFilterKeys}
          wrapWithParent
        />
      )}
      {(display || exportMutation) && (
        <ListDisplayControls
          currentFilters={currentFilters}
          currentSearch={currentSearch}
          display={display}
          exportMutation={exportMutation}
        />
      )}
    </>
  );

  if (wrapWithParent) {
    return controls;
  }

  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-2", className)}>
      {controls}
    </div>
  );
}

export function ListHeaderFiltersRow<$Filters>({
  className,
  ...props
}: ListHeaderControlsProps<$Filters>) {
  return (
    <PageRow
      inset="list"
      divider
      className={cn("min-h-11 flex-wrap justify-start gap-y-1.5 py-1.5", className)}
    >
      <HorizontalOverflowLane
        ariaLabel="Filters and display controls"
        className="w-full"
      >
        <ListHeaderControls
          {...props}
          className="w-max min-w-full flex-nowrap gap-1.5"
          showActiveClauses={false}
          showStandaloneFilters={false}
          wrapWithParent={false}
        />
      </HorizontalOverflowLane>
      <ListQueryControls
        filterConfigs={props.filterConfigs}
        filterComponents={props.filterComponents}
        filterInput={props.filterInput}
        currentFilters={props.currentFilters}
        onFilterChange={props.onFilterChange}
        showTrigger={false}
        showStandaloneFilters={false}
        hiddenFilterKeys={[...(props.hiddenFilterKeys ?? []), ...(props.standaloneFilterKeys ?? [])]}
        wrapWithParent
      />
    </PageRow>
  );
}

export function ListHeaderActiveFiltersRow<$Filters>({
  filterConfigs,
  filterComponents,
  filterInput,
  currentFilters,
  onFilterChange,
  className,
  hiddenFilterKeys = [],
  standaloneFilterKeys = [],
}: ListHeaderActiveFiltersRowProps<$Filters>) {
  const hasFilters = Object.keys(filterConfigs).length > 0;
  const typedCurrentFilters = currentFilters as Record<string, unknown> | undefined;
  const activeHiddenFilterKeys = [...hiddenFilterKeys, ...standaloneFilterKeys];
  const hasVisibleActiveFilterValues = hasActiveConfiguredFilterValues(
    filterConfigs,
    typedCurrentFilters,
    activeHiddenFilterKeys,
  );

  if (!hasFilters || !hasVisibleActiveFilterValues) {
    return null;
  }

  return (
    <PageRow divider className={cn("py-1.5", className)}>
      <ListQueryControls
        filterConfigs={filterConfigs}
        filterComponents={filterComponents}
        filterInput={filterInput}
        currentFilters={currentFilters}
        onFilterChange={onFilterChange}
        showTrigger={false}
        showStandaloneFilters={false}
        hiddenFilterKeys={activeHiddenFilterKeys}
        wrapWithParent
      />
    </PageRow>
  );
}
